import { useCallback } from "react";
import axios, { useApiRequest } from "./axios";
import { saveBlobAs } from "./invoices";
import {
  buildShipmentActionEndpoint,
  FURGONETKA_AUTHORIZE_URL_ENDPOINT,
  FURGONETKA_CONNECT_ENDPOINT,
  FURGONETKA_DISCONNECT_ENDPOINT,
  FURGONETKA_SETTINGS_ENDPOINT,
  FURGONETKA_STATUS_ENDPOINT,
  SHIPMENTS_ENDPOINT,
  SHIPMENTS_QUOTE_ENDPOINT,
} from "../consts";
import { FurgonetkaSettings, ShipmentParcel, ShipmentReceiver } from "../types/shipping";

// Calls that go through to Furgonetka - pricing with every carrier, ordering,
// fetching a label - can take well past the default request timeout.
const FURGONETKA_TIMEOUT = 60000;

/** Remembered across the redirect to Furgonetka and back: the state we sent,
 * and which workspace the connection is for (another tab may switch the
 * workspace in the meantime). */
export const FURGONETKA_OAUTH_STORAGE_KEY = "furgonetkaOAuth";

export interface ShipmentPayload {
  items: { part: number | string; quantity: string }[];
  receiver: ShipmentReceiver;
  parcels: ShipmentParcel[];
  package_type?: string;
  service_id: number;
  client?: number | string | null;
  notes?: string;
}

export const useFurgonetkaAccount = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const getStatus = useCallback(
    async () => await executeRequest(() => axios.get(FURGONETKA_STATUS_ENDPOINT)),
    [executeRequest]
  );

  const getAuthorizeUrl = useCallback(
    async () => await executeRequest(() => axios.post(FURGONETKA_AUTHORIZE_URL_ENDPOINT)),
    [executeRequest]
  );

  const connect = useCallback(
    async (code: string, state: string, tenantId?: string | null) =>
      await executeRequest(() =>
        axios.post(
          FURGONETKA_CONNECT_ENDPOINT,
          { code, state },
          {
            timeout: FURGONETKA_TIMEOUT,
            ...(tenantId ? { headers: { Tenant: tenantId } } : {}),
          }
        )
      ),
    [executeRequest]
  );

  const disconnect = useCallback(
    async () => await executeRequest(() => axios.post(FURGONETKA_DISCONNECT_ENDPOINT)),
    [executeRequest]
  );

  const updateSettings = useCallback(
    async (settings: Partial<FurgonetkaSettings>) =>
      await executeRequest(() => axios.patch(FURGONETKA_SETTINGS_ENDPOINT, settings)),
    [executeRequest]
  );

  return { loading, error, getStatus, getAuthorizeUrl, connect, disconnect, updateSettings };
};

export const useShipments = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const getShipment = useCallback(
    async (id: string | number) => await executeRequest(() => axios.get(`${SHIPMENTS_ENDPOINT}${id}/`)),
    [executeRequest]
  );

  const quote = useCallback(
    async (receiver: ShipmentReceiver, parcels: ShipmentParcel[]) =>
      await executeRequest(() =>
        axios.post(SHIPMENTS_QUOTE_ENDPOINT, { receiver, parcels }, { timeout: FURGONETKA_TIMEOUT })
      ),
    [executeRequest]
  );

  const createShipment = useCallback(
    async (payload: ShipmentPayload) =>
      await executeRequest(() => axios.post(SHIPMENTS_ENDPOINT, payload, { timeout: FURGONETKA_TIMEOUT })),
    [executeRequest]
  );

  const cancelShipment = useCallback(
    async (id: string | number) =>
      await executeRequest(() =>
        axios.post(buildShipmentActionEndpoint(id, "cancel"), {}, { timeout: FURGONETKA_TIMEOUT })
      ),
    [executeRequest]
  );

  const refreshTracking = useCallback(
    async (id: string | number) =>
      await executeRequest(() =>
        axios.post(buildShipmentActionEndpoint(id, "refresh-tracking"), {}, { timeout: FURGONETKA_TIMEOUT })
      ),
    [executeRequest]
  );

  const returnToStock = useCallback(
    async (id: string | number) =>
      await executeRequest(() => axios.post(buildShipmentActionEndpoint(id, "return-to-stock"))),
    [executeRequest]
  );

  const downloadLabel = useCallback(
    async (id: string | number, waybill?: string | null) => {
      const { data, success, errorData } = await executeRequest(() =>
        axios.get(buildShipmentActionEndpoint(id, "label"), {
          responseType: "blob",
          timeout: FURGONETKA_TIMEOUT,
        })
      );

      if (!success || !data) {
        return { success: false, errorData };
      }

      saveBlobAs(data, `etykieta-${waybill || id}.pdf`);
      return { success: true };
    },
    [executeRequest]
  );

  return {
    loading,
    error,
    getShipment,
    quote,
    createShipment,
    cancelShipment,
    refreshTracking,
    returnToStock,
    downloadLabel,
  };
};

/** The message of a failed request, whatever shape the error came in. */
export const errorMessageOf = (errorData: any, fallback: string): string => {
  if (!errorData || typeof errorData === "string") {
    return errorData || fallback;
  }
  return errorData.message || errorData.detail || fallback;
};
